/**
 * Compact relative-time formatter for muted "last-accessed" badges in drawer
 * rows, chat activity strips, etc. Slack-style: "3m", "2h", "1d", "Apr 12".
 *
 * Not locale-aware for the <7d range (numeric + single-letter unit is universal).
 * For older items, falls back to the user's locale short-date format.
 *
 * Returns empty string for null/invalid input so call-sites can render
 * `{formatCompactTime(iso)}` without conditional wrapping.
 */
export function formatCompactTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Full locale-formatted timestamp for tooltips (`title=` attribute).
 * Returns empty string on null/invalid input.
 */
export function formatFullTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
}
